import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useRef, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

import heroImage from "@/assets/ibiza-nights-hero-page.webp";
import logoHi from "@/assets/logo-hi-ibiza.webp";
import logoUshuaia from "@/assets/logo-ushuaia.webp";
import logoPacha from "@/assets/logo-pacha.webp";
import logoAmnesia from "@/assets/logo-amnesia.webp";
import logoDc10 from "@/assets/logo-dc10.webp";
import logoLio from "@/assets/logo-lio.webp";
import logoClubChinois from "@/assets/logo-club-chinois.webp";

const ease = [0.22, 1, 0.36, 1] as const;

/* ─── DATA ─── */

const arrangements = [
  {
    title: "Guest List Access",
    description:
      "Priority guest list access to Ibiza's leading clubs, allowing you to move through entry with ease, even during the busiest nights. Each request is arranged in advance, ensuring that your arrival is smooth, coordinated, and aligned with your evening plans.",
  },
  {
    title: "VIP Table Bookings",
    description:
      "Private tables arranged at the island's most sought-after venues, offering a more refined and comfortable way to experience the night. From table selection to timing and overall setup, every detail is handled to match your preferences and group size.",
  },
  {
    title: "Club Tickets",
    description:
      "Access to high-demand events, including sold-out nights and headline performances across Ibiza's top venues. Whether planned in advance or requested last-minute, we ensure that entry is secured without unnecessary complications.",
  },
  {
    title: "Transport Coordination",
    description:
      "Private drivers and VIP transport arranged throughout your evening, ensuring seamless movement between your residence, dinner reservations, and nightlife venues. Every transfer is aligned with your schedule, allowing your night to unfold without interruption.",
  },
  {
    title: "Venue Selection & Planning",
    description:
      "Guidance on the most suitable venues based on your preferences, group, and the atmosphere you are looking for. From high-energy performances to more refined settings, we ensure that each choice aligns with the experience you want to create.",
  },
  {
    title: "Last-Minute Arrangements",
    description:
      "Flexibility to accommodate changes or spontaneous decisions throughout the evening. Whether adjusting plans, securing additional access, or arranging transport, our team remains available to handle requests in real time.",
  },
];

const venues = [
  { name: "Hï Ibiza", slug: "hi-ibiza", tagline: "The world's number one club", logo: logoHi },
  { name: "Ushuaïa", slug: "ushuaia", tagline: "Open-air daylight performances", logo: logoUshuaia },
  { name: "Pacha", slug: "pacha", tagline: "The icon of Ibiza nightlife", logo: logoPacha },
  { name: "Amnesia", slug: "amnesia", tagline: "Underground energy since 1976", logo: logoAmnesia },
  { name: "DC-10", slug: "dc10", tagline: "Raw, unfiltered club culture", logo: logoDc10 },
  { name: "Lío", slug: "lio", tagline: "Cabaret meets nightlife", logo: logoLio },
  { name: "Club Chinois", slug: "club-chinois", tagline: "Asia-inspired refined nightlife", logo: logoClubChinois },
];

/* ─── PARTNER SLIDER ─── */

const PartnerSlider = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener("scroll", updateScrollState);
    return () => el.removeEventListener("scroll", updateScrollState);
  }, []);

  const scroll = (dir: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === "left" ? -300 : 300, behavior: "smooth" });
  };

  return (
    <div className="relative">
      {canScrollLeft && (
        <button
          onClick={() => scroll("left")}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 h-10 w-10 flex items-center justify-center bg-background/80 backdrop-blur border border-foreground/10 text-foreground hover:bg-foreground/5 transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      )}
      {canScrollRight && (
        <button
          onClick={() => scroll("right")}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 h-10 w-10 flex items-center justify-center bg-background/80 backdrop-blur border border-foreground/10 text-foreground hover:bg-foreground/5 transition-colors"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      )}
      <div
        ref={scrollRef}
        className="flex gap-4 md:gap-6 overflow-x-auto scrollbar-hide px-1 py-2"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {venues.map((venue) => (
          <Link
            key={venue.slug}
            to={`/ibiza/nights/${venue.slug}`}
            className="group flex-shrink-0 w-[160px] md:w-[180px] flex flex-col items-center text-center py-8 px-4 hover:opacity-70 transition-all duration-700"
          >
            <div className="h-14 md:h-16 flex items-center justify-center mb-4">
              <img
                src={venue.logo}
                alt={venue.name}
                className="max-h-full max-w-[120px] object-contain"
              />
            </div>
            <p className="font-body text-muted-foreground text-[10px] italic leading-relaxed">
              {venue.tagline}
            </p>
          </Link>
        ))}
      </div>
    </div>
  );
};

/* ─── PAGE ─── */

const IbizaNights = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navigation />

      {/* Hero */}
      <section className="relative h-screen flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt="Ibiza nightlife scene"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/30 to-foreground/10" />
        </div>
        <div className="relative z-10 container-luxury pb-20 md:pb-28">
          <motion.span
            className="font-label text-bronze block mb-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 1.2, ease }}
          >
            Ibiza
          </motion.span>
          <motion.h1
            className="font-display text-4xl md:text-6xl lg:text-7xl text-primary-foreground tracking-wider uppercase mb-4"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 1.4, ease }}
          >
            Arzani Nights
          </motion.h1>
          <motion.p
            className="font-accent text-primary-foreground/50 text-sm md:text-base italic max-w-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 1.4, ease }}
          >
            The night defines a different side of the island.
          </motion.p>
        </div>
      </section>

      {/* Opening */}
      <section className="section-spacing bg-background">
        <div className="container-luxury max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.4, ease }}
          >
            <span className="font-label text-bronze block mb-6">The Experience</span>
            <div className="divider-subtle mb-10" />
            <p className="font-body text-muted-foreground text-sm md:text-base leading-[1.9] mb-8">
              Through Arzani Nights, every detail is arranged to give you effortless access to
              Ibiza's most sought-after venues. From guest list entry to VIP tables and private
              transport, each element is secured in advance — allowing your evening to unfold
              without interruption.
            </p>
            <p className="font-body text-muted-foreground text-sm md:text-base leading-[1.9]">
              From arrival to departure, your night is handled with precision, discretion, and
              complete attention to detail.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Deeper text */}
      <section className="py-16 md:py-24 bg-secondary/30">
        <div className="container-luxury max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.2, ease }}
          >
            <p className="font-accent text-foreground/70 text-base md:text-lg italic leading-[2] text-center">
              Ibiza's nightlife is defined by energy, atmosphere, and access. Knowing where to go
              is only part of the experience — knowing how to move through it is what defines it.
            </p>
            <p className="font-accent text-foreground/70 text-base md:text-lg italic leading-[2] text-center mt-6">
              Through Arzani, every step is aligned. Entry is secured, tables are prepared, and
              transport is arranged, ensuring that each moment flows naturally from one setting to
              the next.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Arrangements */}
      <section className="section-spacing bg-background">
        <div className="container-luxury">
          <motion.div
            className="mb-16 md:mb-24"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze block mb-4">What We Arrange</span>
            <h2 className="font-display text-3xl md:text-5xl text-foreground tracking-wide uppercase">
              Our Services
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-16 md:gap-y-20">
            {arrangements.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 1.2, delay: i * 0.08, ease }}
              >
                <span className="font-label text-bronze/60 text-[10px] block mb-3">
                  0{i + 1}
                </span>
                <h3 className="font-display-medium text-xl md:text-2xl text-foreground tracking-wide uppercase mb-4">
                  {item.title}
                </h3>
                <p className="font-body text-muted-foreground text-sm leading-[1.9] mb-6">
                  {item.description}
                </p>
                <a
                  href="https://wa.me/message/2XEKJ5STR2IZJ1"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-8 py-3 border border-foreground/20 text-foreground font-label tracking-[0.2em] text-[10px] hover:bg-foreground hover:text-background transition-all duration-500"
                >
                  Choose
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Venues */}
      <section className="section-spacing bg-secondary/30">
        <div className="container-luxury">
          <motion.div
            className="mb-12 md:mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze block mb-4">Our Partners</span>
            <h2 className="font-display text-3xl md:text-5xl text-foreground tracking-wide uppercase mb-6">
              Venue Partners
            </h2>
            <p className="font-body text-muted-foreground text-sm md:text-base leading-[1.9] max-w-2xl">
              Access to Ibiza's most iconic nightlife destinations. Each venue offers a different
              atmosphere — from open-air performances to underground scenes and refined nightlife
              experiences.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease }}
          >
            <PartnerSlider />
          </motion.div>
        </div>
      </section>

      {/* Closing Statement */}
      <section className="py-28 md:py-40 bg-warm-black">
        <div className="container-luxury text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.6, ease }}
          >
            <p className="font-display text-2xl md:text-4xl text-primary-foreground tracking-wide uppercase leading-[1.4] mb-4">
              No waiting.
            </p>
            <p className="font-display text-2xl md:text-4xl text-primary-foreground tracking-wide uppercase leading-[1.4] mb-4">
              No uncertainty.
            </p>
            <p className="font-display text-2xl md:text-4xl text-primary-foreground tracking-wide uppercase leading-[1.4] mb-10">
              Just access.
            </p>
            <p className="font-label text-bronze tracking-[0.4em] text-sm">A R Z A N I</p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing bg-background">
        <div className="container-luxury max-w-2xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze block mb-6">Get Started</span>
            <p className="font-body text-muted-foreground text-sm leading-[1.9] mb-10">
              Tell us the evening you want to create, and we will arrange every detail.
            </p>
            <a
              href="https://wa.me/message/2XEKJ5STR2IZJ1"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-10 py-3 border border-foreground/20 text-foreground text-xs tracking-[0.2em] uppercase font-label hover:bg-foreground hover:text-background transition-all duration-500"
            >
              Contact Us
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default IbizaNights;
